import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import "./Trackorder.css";
import { FaTruck, FaBox } from "react-icons/fa";
import Bannerheader from "../Bannerheader";

const Trackorder = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("accessToken");
      const userId = localStorage.getItem("userId");

      if (!token || !userId) {
        navigate("/AuthForm");
        return;
      }

      const response = await fetch(
        `https://vogelkoparchitects.com/api/ecom/track-orders-by-user/${userId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userId");
        localStorage.removeItem("username");
        setError("Your session has expired. Please login again.");
        setTimeout(() => navigate("/AuthForm"), 2000);
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch orders");

      const data = await response.json();
      setOrders(data || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const userId = localStorage.getItem("userId");

    if (!token || !userId) {
      navigate("/AuthForm");
      return;
    }
    fetchOrders();
  }, [navigate, fetchOrders]);

  const toggleOrderDetails = (orderNo) => {
    setExpandedOrder(expandedOrder === orderNo ? null : orderNo);
  };

  const handleTrackPackage = (trackingLink) => {
    window.open(trackingLink, "_blank", "noopener noreferrer");
  };

  const handleContinueShopping = () => navigate("/Productcategory");



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleLogin = () => navigate("/AuthForm");

  if (loading) {
    return (
      <div className="track-loading-container">
        <div className="loading-spinner"></div>
        <p>Loading your orders...</p>
      </div>
    );
  }

  return (
    <>
      <Bannerheader />

      <div className="track-order-container">
        {error && (
          <div className="track-error-message">
            {error}
            {error.includes("session has expired") ? (
              <button onClick={handleLogin} className="retry-btn">
                Go to Login
              </button>
            ) : (
              <button onClick={fetchOrders} className="retry-btn">
                Retry
              </button>
            )}
          </div>
        )}

        {!loading && orders.length === 0 && !error ? (
          <div className="no-orders">
            <div className="no-orders-icon">📦</div>
            <h2 className="no-orders-title">No Orders Found</h2>
            <p className="no-orders-message">
              You haven't placed any orders yet. Start shopping to track your
              orders!
            </p>
            <button
              className="continue-shopping-btn"
              onClick={handleContinueShopping}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          orders.length > 0 && (
            <div className="orders-list">
              {orders.map((order) => (
                <div key={order.order_no} className="order-card">
                  {/* Order Header */}
                  <div
                    className="order-header"
                    onClick={() => toggleOrderDetails(order.order_no)}
                  >
                    <div className="order-header-left">
                      <div className="order-icon">
                        <FaBox />
                      </div>
                      <div className="order-info">
                        <h3 className="order-number">
                          Order #{order.order_no}
                        </h3>
                        <div className="order-meta">
                          <span className="order-items-count">
                            {order.items.length} item(s)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="order-header-right">
                      <span className="expand-icon">
                        {expandedOrder === order.order_no ? "▼" : "▶"}
                      </span>
                    </div>
                  </div>

                  {/* Tracking Info */}
                  <div className="tracking-info">
                    <div className="delivery-partner">
                      <FaTruck className="tracking-icon" />
                      <span className="partner-name">
                        {order.delivery_partner}
                      </span>
                    </div>
                    <div className="tracking-number">
                      <span className="tracking-label">Tracking #:</span>
                      <span className="tracking-value">
                        {order.tracking_no}
                      </span>
                    </div>
                    <button
                      className="track-package-btn"
                      onClick={() => handleTrackPackage(order.tracking_link)}
                    >
                      Track Package
                    </button>
                    <p className="tracking-message">{order.message}</p>
                  </div>

                  {/* Expanded Order Details */}
                  {expandedOrder === order.order_no && (
                    <div className="order-details">
                      <h4 className="order-details-title">Order Items</h4>
                      <div className="order-items">
                        {order.items.map((item, index) => (
                          <div key={index} className="order-item">
                            <div className="item-image">
                              <img
                                src={item.product_image}
                                alt={item.product_name}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/80x80?text=Product";
                                }}
                              />
                            </div>
                            <div className="item-details">
                              <h5 className="item-name">{item.product_name}</h5>
                              <div className="item-specs">
                                <span className="item-quantity">
                                  Qty: {item.quantity}
                                </span>
                                <span className="item-price">
                                  {formatCurrency(item.price_per_unit)} each
                                </span>
                              </div>
                              <div className="item-total">
                                Total: {formatCurrency(item.total_price)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Order Summary */}
                      <div className="order-summary">
                        <div className="summary-row">
                          <span>Total Items:</span>
                          <span>
                            {order.items.reduce(
                              (sum, item) => sum + item.quantity,
                              0,
                            )}
                          </span>
                        </div>
                        <div className="summary-row total">
                          <span>Order Total:</span>
                          <span>
                            {formatCurrency(
                              order.items.reduce(
                                (sum, item) => sum + item.total_price,
                                0,
                              ),
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {/* Continue Shopping Button */}
        {orders.length > 0 && (
          <div className="track-footer">
            <button
              className="continue-shopping-link"
              onClick={handleContinueShopping}
            >
              ← Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Trackorder;
